/**
 * GameMessage 命令式调用
 */

import { createApp, h, type App as VueApp } from 'vue'
import GameMessageItem from './GameMessageItem.vue'
import type { GameMessageOptions, GameMessageInstance, GameMessageHandler } from './types'

/** 消息实例列表 */
const instances: GameMessageInstance[] = []

/** 消息计数器(用于生成唯一 ID) */
let seed = 0

/**
 * 创建消息实例
 */
function createMessage(options: GameMessageOptions): GameMessageHandler {
  const {
    message,
    type = 'info',
    duration = 3000,
    showClose = false,
    onClose,
    closeOthers = false,
    customColor,
  } = options

  // 如果需要关闭其他消息
  if (closeOthers) {
    closeAll()
  }

  // 生成唯一 ID
  const id = `game-message-${seed++}`

  // 创建消息实例数据
  const instance: GameMessageInstance = {
    id,
    message,
    type,
    duration,
    showClose,
    onClose: onClose || (() => {}),
    visible: true,
    customColor,
  }

  // 添加到实例列表
  instances.push(instance)

  // 创建挂载容器
  const container = document.createElement('div')
  container.className = `game-message-container-${id}`
  document.body.appendChild(container)

  // 创建 Vue 应用实例
  let app: VueApp | null = null

  const close = () => {
    const index = instances.findIndex(item => item.id === id)
    if (index === -1) return

    const removedInstance = instances[index]
    if (!removedInstance) return

    instances.splice(index, 1)

    // 调用关闭回调
    removedInstance.onClose()

    // 销毁 Vue 应用
    if (app) {
      setTimeout(() => {
        app?.unmount()
        app = null
        container.remove()
      }, 300) // 等待动画结束
    }
  }

  const destroy = () => {
    close()
  }

  // 创建组件
  app = createApp({
    render() {
      return h(GameMessageItem, {
        id: instance.id,
        message: instance.message,
        type: instance.type,
        duration: instance.duration,
        showClose: instance.showClose,
        customColor: instance.customColor,
        onClose: close,
        onDestroy: destroy,
      })
    },
  })

  // 挂载到容器
  app.mount(container)

  return { close }
}

/**
 * 关闭所有消息
 */
function closeAll() {
  // 从后往前关闭,避免索引错乱
  for (let i = instances.length - 1; i >= 0; i--) {
    const instance = instances[i]
    if (!instance) continue

    const container = document.querySelector(`.game-message-container-${instance.id}`)
    const messageEl = container?.querySelector('.game-message-item') as HTMLElement
    if (messageEl) {
      // 触发关闭
      const closeBtn = messageEl.querySelector('.game-message-close') as HTMLElement
      if (closeBtn) {
        closeBtn.click()
      }
    }
  }
}

/**
 * 游戏消息工具类
 */
class GameMessageClass {
  /**
   * 显示信息提示 (黑金背景)
   */
  info(message: string, options: Omit<GameMessageOptions, 'message' | 'type'> = {}): GameMessageHandler {
    return createMessage({ message, type: 'info', ...options })
  }

  /**
   * 显示错误提示 (红色背景)
   */
  error(message: string, options: Omit<GameMessageOptions, 'message' | 'type'> = {}): GameMessageHandler {
    return createMessage({ message, type: 'error', ...options })
  }

  /**
   * 显示成功提示
   */
  success(message: string, options: Omit<GameMessageOptions, 'message' | 'type'> = {}): GameMessageHandler {
    return createMessage({ message, type: 'success', ...options })
  }

  /**
   * 显示警告提示
   */
  warning(message: string, options: Omit<GameMessageOptions, 'message' | 'type'> = {}): GameMessageHandler {
    return createMessage({ message, type: 'warning', ...options })
  }

  /**
   * 关闭所有消息
   */
  closeAll(): void {
    closeAll()
  }
}

/**
 * 游戏消息工具实例
 *
 * @example
 * ```typescript
 * // 显示信息提示 (黑金背景)
 * GameMessage.info('游戏开始')
 *
 * // 显示错误提示 (红色背景)
 * GameMessage.error('下注失败')
 *
 * // 显示新消息时关闭之前的消息
 * GameMessage.info('第一条消息', { closeOthers: true })
 *
 * // 同时显示多条消息
 * GameMessage.info('消息1')
 * GameMessage.info('消息2')
 * GameMessage.info('消息3')
 *
 * // 关闭所有消息
 * GameMessage.closeAll()
 * ```
 */
export const GameMessage = new GameMessageClass()

// 导出类型
export type { GameMessageOptions, GameMessageHandler } from './types'
